import React from "react";
import styled from "styled-components";
import { read_timeline_date_label } from "../../../utils/timeline/timeline_formatting";
import { Timeline_Sales_Session_Callback } from "./Timeline_Sales_Session_Callback";
import { Timeline_Sales_Session_Comment } from "./Timeline_Sales_Session_Comment";
import { Timeline_Sales_Session_Edit } from "./Timeline_Sales_Session_Edit";
import { Timeline_Sales_Session_Status } from "./Timeline_Sales_Session_Status";
import {
    read_sales_session_events,
    read_sales_session_outcome,
    read_sales_session_seller
} from "./sales_session_view";

const salesOutcomeStyleLookup = {
    Agency: {
        color: "#56b9a9",
        background: "rgba(86, 185, 169, 0.12)"
    },
    Sass: {
        color: "#6b8afd",
        background: "rgba(107, 138, 253, 0.12)"
    },
    Lost: {
        color: "#e05b67",
        background: "rgba(224, 91, 103, 0.12)"
    },
    Update: {
        color: "#9b7bd5",
        background: "rgba(155, 123, 213, 0.12)"
    }
};

export const TimelineSalesSession = styled.section`
    display: grid;
    min-width: 0;
    overflow: hidden;
    border: 0.1rem solid ${p => p.theme.mid};
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    font-family: "fontMedium", sans-serif;
`;

export const TimelineSalesHeader = styled.header`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.8rem 1.2rem;
    min-width: 0;
    padding: 1rem;
    border-bottom: ${p => p.$hasEvents ? `0.1rem solid ${p.theme.low}` : "0"};
    background: ${p => p.theme.background};
`;

export const TimelineSalesSeller = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.45rem 0.7rem;
    min-width: 0;
`;

export const TimelineSalesSellerText = styled.div`
    min-width: 0;

    h4 {
        color: ${p => p.theme.color};
        font-family: "fontBold", sans-serif;
        font-size: 1.35rem;
        font-weight: 400;
        overflow-wrap: anywhere;
    }
`;

export const TimelineSalesOutcome = styled.div`
    display: inline-flex;
    align-items: center;
    align-self: center;
    max-width: 100%;
    padding: 0.3rem 0.7rem;
    border: 0.1rem solid ${p => p.$statusStyle.color};
    border-radius: var(--smallRadius);
    color: ${p => p.$statusStyle.color};
    background: ${p => p.$statusStyle.background};
    font-family: "fontMedium", sans-serif;
    font-size: 1.15rem;
    overflow-wrap: anywhere;

    h6 {
        color: inherit;
        font-family: inherit;
        font-size: inherit;
    }
`;

export const TimelineSalesTimestamp = styled.time`
    color: ${p => p.theme.color};
    font-family: "fontMedium", sans-serif;
    font-size: 1.1rem;
    white-space: normal;
`;

export const TimelineSalesEvents = styled.div`
    display: grid;
    min-width: 0;

    > * + * {
        border-top: 0.1rem solid ${p => p.theme.low};
    }
`;

const salesSessionScenarioLookup = {
    callback: Timeline_Sales_Session_Callback,
    comment: Timeline_Sales_Session_Comment,
    edit: Timeline_Sales_Session_Edit,
    status: Timeline_Sales_Session_Status
};

// Composes the readable audit card for one completed Sales-board session.
export const Timeline_Sales_Session = (props) => {
    const seller = read_sales_session_seller(props.event, props.teamMembers);
    const outcome = read_sales_session_outcome(props.event);
    const outcomeStyle = salesOutcomeStyleLookup[outcome];
    const closedAt = props.event?.data?.closedAt || props.event?.to?.closedAt;
    const events = read_sales_session_events(props.event);

    return (
        <TimelineSalesSession data-testid="timeline-sales-session" aria-label={props.t("Completed Sales session")}>
            <TimelineSalesHeader $hasEvents={Boolean(events.length)}>
                <TimelineSalesSeller>
                    <TimelineSalesSellerText>
                        <h4>{seller.name}</h4>
                    </TimelineSalesSellerText>
                    <TimelineSalesOutcome
                        $statusStyle={outcomeStyle}
                        data-testid="timeline-sales-outcome"
                        title={`${props.t("Outcome")}: ${props.t(outcome)}`}
                    >
                        <h6>{props.t(outcome)}</h6>
                    </TimelineSalesOutcome>
                </TimelineSalesSeller>
                <TimelineSalesTimestamp dateTime={closedAt}>
                    {read_timeline_date_label(closedAt)}
                </TimelineSalesTimestamp>
            </TimelineSalesHeader>
            {Boolean(events.length) && (
                <TimelineSalesEvents>
                    {events.map((event) => {
                        const TimelineSalesScenario = salesSessionScenarioLookup[event.scenario];

                        return (
                            <TimelineSalesScenario
                                key={event.key}
                                event={event}
                                t={props.t}
                            />
                        );
                    })}
                </TimelineSalesEvents>
            )}
        </TimelineSalesSession>
    );
};
